import { z } from "zod"

import { roleSchema, vertragsmodellSchema } from "./responses"

/**
 * Admin catalogue, second part: interface, reporting, contracts, roles.
 * Aggregates and settings only, no personal data.
 */

// ---------------------------------------------------------------------------
// Interface: labels, images, start page, news
// ---------------------------------------------------------------------------

/** The advisor menu entries whose label the platform may change. */
export const NAV_EINTRAEGE = [
  { key: "start", label: "Start" },
  { key: "chat", label: "Chat mit {{coach}}" },
  { key: "gespraechsauftakt", label: "Terminvorbereitung" },
  { key: "durchlauf", label: "Gesprächstraining" },
  { key: "nachbereitung", label: "Nachbereitung" },
  { key: "produkttraining", label: "Produkttraining" },
  { key: "pruefung", label: "Prüfungsvorbereitung" },
] as const
export type NavKey = (typeof NAV_EINTRAEGE)[number]["key"]

/** Image slots of the advisor interface, with their built-in file. */
export const BILD_SLOTS = [
  { key: "produkt-aktivplan", label: "Produkt Aktivplan", standard: "/mediathek/produkt-aktivplan.jpg" },
  { key: "produkt-basisrente", label: "Produkt Basisrente", standard: "/mediathek/produkt-basisrente.jpg" },
  { key: "produkt-berufsunfaehigkeit", label: "Produkt Einkommensschutz", standard: "/mediathek/produkt-berufsunfaehigkeit.jpg" },
  { key: "produkt-foerderrente", label: "Produkt Förderrente", standard: "/mediathek/produkt-foerderrente.jpg" },
  { key: "filiale", label: "Startseite Filiale", standard: "/mediathek/filiale.jpg" },
] as const
export type BildSlot = (typeof BILD_SLOTS)[number]["key"]

/** Relative paths under the app or https URLs; nothing that runs script. */
const bildUrlSchema = z
  .string()
  .trim()
  .regex(/^(\/[\w./-]+|https:\/\/[^\s"'<>]+)$/, "Pfad wie /mediathek/bild.jpg oder https-Adresse.")

export const oberflaecheSchema = z.object({
  navLabels: z.record(z.string(), z.string()),
  bilder: z.record(z.string(), z.string()),
  updatedAt: z.string().optional(),
  updatedBy: z.string().optional(),
})
export type Oberflaeche = z.infer<typeof oberflaecheSchema>

export const updateOberflaecheSchema = z.object({
  navLabels: z
    .record(z.string(), z.string().trim().max(40))
    .refine((r) => Object.keys(r).every((k) => NAV_EINTRAEGE.some((e) => e.key === k)), {
      message: "Unbekannter Menüeintrag.",
    })
    .optional(),
  bilder: z
    .record(z.string(), z.union([bildUrlSchema, z.literal("")]))
    .refine((r) => Object.keys(r).every((k) => BILD_SLOTS.some((s) => s.key === k)), {
      message: "Unbekannter Bildplatz.",
    })
    .optional(),
})
export type UpdateOberflaecheInput = z.infer<typeof updateOberflaecheSchema>

export const upsertNeuigkeitSchema = z.object({
  id: z.string().optional(),
  titel: z.string().trim().min(2).max(160),
  text: z.string().trim().max(4000).default(""),
  link: z.union([z.url({ protocol: /^https$/ }), z.literal("")]).default(""),
  ticker: z.boolean().default(false),
  sichtbarAb: z.iso.datetime({ offset: true }).optional(),
  sichtbarBis: z.union([z.iso.datetime({ offset: true }), z.literal("")]).optional(),
  /** Absent: every Sparkasse. */
  tenantId: z.string().optional(),
})
export type UpsertNeuigkeitInput = z.infer<typeof upsertNeuigkeitSchema>

export const neuigkeitSchema = z.object({
  id: z.string(),
  titel: z.string(),
  text: z.string(),
  link: z.string(),
  ticker: z.boolean(),
  sichtbarAb: z.string(),
  sichtbarBis: z.string().optional(),
  tenantId: z.string().optional(),
  updatedAt: z.string(),
  updatedBy: z.string(),
})
export type Neuigkeit = z.infer<typeof neuigkeitSchema>
export const neuigkeitListSchema = z.array(neuigkeitSchema)

// ---------------------------------------------------------------------------
// Reporting, contracts and billing
// ---------------------------------------------------------------------------

export const kpiZeileSchema = z.object({
  tenantId: z.string(),
  name: z.string(),
  nutzer: z.number().int(),
  aktiveNutzer: z.number().int(),
  lizenzen: z.number().int(),
  /** Active users per licensed seat; absent without seats. */
  auslastung: z.number().optional(),
  trainings: z.number().int(),
  aktiveMinuten: z.number().int(),
  /** Average active minutes per active user. */
  minutenJeAktivem: z.number(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int(),
  kostenCent: z.number().int(),
})
export type KpiZeile = z.infer<typeof kpiZeileSchema>

export const kpiBerichtSchema = z.object({
  von: z.string(),
  bis: z.string(),
  zeilen: z.array(kpiZeileSchema),
  summe: kpiZeileSchema.omit({ tenantId: true, name: true }),
})
export type KpiBericht = z.infer<typeof kpiBerichtSchema>

export const reportIntervallSchema = z.enum(["woechentlich", "monatlich"])
export type ReportIntervall = z.infer<typeof reportIntervallSchema>

export const createReportAboSchema = z.object({
  empfaenger: z.email(),
  /** Absent: all customer tenants in one report, platform only. */
  tenantId: z.string().optional(),
  intervall: reportIntervallSchema,
})
export type CreateReportAboInput = z.infer<typeof createReportAboSchema>

export const reportAboSchema = z.object({
  id: z.string(),
  empfaenger: z.string(),
  tenantId: z.string().optional(),
  intervall: reportIntervallSchema,
  aktiv: z.boolean(),
  naechsterVersand: z.string(),
  letzterVersand: z.string().optional(),
  createdBy: z.string(),
})
export type ReportAbo = z.infer<typeof reportAboSchema>
export const reportAboListSchema = z.array(reportAboSchema)

export const VERTRAGSMODELL_LABELS: Record<z.infer<typeof vertragsmodellSchema>, string> = {
  pauschal: "Pauschale",
  je_lizenz: "Pauschale plus je Lizenz",
  je_aktivem_nutzer: "Pauschale plus je aktivem Nutzer",
}

export const abrechnungSchema = z.object({
  /** YYYY-MM */
  monat: z.string(),
  zeilen: z.array(
    z.object({
      tenantId: z.string(),
      name: z.string(),
      vertragsmodell: vertragsmodellSchema,
      pauschaleEur: z.number(),
      preisJeEinheitEur: z.number(),
      /** Seats or active users of the month, depending on the model. */
      menge: z.number().int(),
      betragEur: z.number(),
      abrechnungsKontakt: z.string(),
      /** False when the month lies outside the contract term. */
      imVertrag: z.boolean(),
    }),
  ),
  summeEur: z.number(),
})
export type Abrechnung = z.infer<typeof abrechnungSchema>

// ---------------------------------------------------------------------------
// Roles
// ---------------------------------------------------------------------------

export const rechtStufeSchema = z.enum(["alle", "mandant", "gruppe", "eigene", "lesen", "keine"])
export type RechtStufe = z.infer<typeof rechtStufeSchema>

/** Read from the CASL rules on the server, so the page cannot drift from them. */
export const rollenMatrixSchema = z.object({
  bereiche: z.array(z.object({ key: z.string(), label: z.string() })),
  rollen: z.array(
    z.object({
      rolle: roleSchema,
      label: z.string(),
      beschreibung: z.string(),
      rechte: z.record(z.string(), rechtStufeSchema),
    }),
  ),
})
export type RollenMatrix = z.infer<typeof rollenMatrixSchema>
